import * as React from "react";

import type { Activity, GetActivity } from "./activity";
import {
  type ActivityLoader,
  useActivityLoader,
  useActivityResult,
} from "./ActivityLoader";
import {
  ALL_TIME,
  type FetchItems,
  type TimelineDataStreamer,
} from "./TimelineDataStreamer";
import { useStreamer } from "./useStreamer";
import type { TimeWindow } from "./window";

/**
 * How many slices the overview strip asks the history to be split into. The
 * strip draws one line per slice with any activity, so the count is high
 * (roughly one per few pixels of a wide strip): busy stretches pack their
 * lines tightly and quiet ones leave gaps.
 */
const ACTIVITY_BUCKETS = 600;

export interface TimelineSources<T> {
  /** The items source on screen; `null` until its first effect has run. */
  streamer: TimelineDataStreamer<T> | null;
  /** The history source on screen; `null` until its first effect has run. */
  activityLoader: ActivityLoader | null;
  /** The whole history once `activityLoader` has it; `null` while pending or failed. */
  history: Activity | null;
  /** A newer `getData` or `getActivity` is loading behind the ones on screen. */
  updating: boolean;
}

/**
 * The timeline's two data sources, built from the page's `getData` and
 * `getActivity`.
 *
 * New `getData` / `getActivity` (new filters, say) are adopted only once
 * they have loaded: until then React keeps rendering the previous ones, and
 * `updating` is true so the caller can dim what is on screen and it does not
 * read as the new answer.
 *
 * The first chunk does not wait for the activity: with all-of-time chunks it
 * does not depend on the window, so it is asked for as soon as the streamer
 * exists, for `initialWindow` (read once) or else all of time.
 */
export function useTimelineSources<T>(
  getData: FetchItems<T>,
  getActivity: GetActivity,
  initialWindow: TimeWindow | undefined,
): TimelineSources<T> {
  const [prefetch] = React.useState(() => initialWindow ?? ALL_TIME);
  const latestStreamer = useStreamer(getData, prefetch);
  const streamer = React.useDeferredValue(latestStreamer);
  const latestActivityLoader = useActivityLoader(getActivity, ACTIVITY_BUCKETS);
  const activityLoader = React.useDeferredValue(latestActivityLoader);
  const activity = useActivityResult(activityLoader);
  return {
    streamer,
    activityLoader,
    history: activity.status === "success" ? activity.activity : null,
    updating:
      streamer !== latestStreamer || activityLoader !== latestActivityLoader,
  };
}
